import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../features/auth/store'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { MerchantPicker } from './MerchantPicker'

interface SidebarProps {
  /** Called when navigating - used to close mobile sidebar */
  onNavigate?: () => void
}

export const Sidebar = ({ onNavigate }: SidebarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuthStore()
  const { hasPermission, hasAnyPermission } = usePermissions()

  // A user manages merchant-scoped pages "per-merchant" only if they can open the merchant
  // detail page — i.e. they hold MERCHANT_VIEW. That (not roleScope) is the correct gate:
  // COLLECTIVE_USER is COLLECTIVE-scoped but has NO MERCHANT_VIEW, so hiding these from them
  // would strand the pages (they can't reach the merchant-detail fallback).
  const canManageViaMerchantDetail = hasPermission(Permission.MERCHANT_VIEW)

  // `group: undefined` renders above/outside any section (Dashboard). Groups render in this
  // array's order, with a collapsible header only when they have ≥1 permission-visible item.
  //
  // `merchantScoped: true` = a page that is inert until a single merchant is chosen (it shows
  // a "Select a merchant…" empty state). TWO different views by role:
  //   • Users WITHOUT MERCHANT_VIEW (MERCHANT admins/users, COLLECTIVE_USER) have no merchant
  //     list — these ARE their sidebar, auto-scoped to their own/selected merchant, so they
  //     stay visible.
  //   • Users WITH MERCHANT_VIEW (COLLECTIVE admins) manage these PER-MERCHANT from the
  //     merchant detail page, so they're hidden from their sidebar (which would force a picker).
  // Cross-merchant/aggregate pages (Orders, Products, Invoices, Payments, Disputes,
  // Reconciliation) are NOT merchantScoped — they stay for everyone with the permission.
  //
  // Storefront provisioning is intentionally NOT here — it's a COLLECTIVE action on the
  // merchant list + a status card in merchant detail. Product Metadata likewise lives in
  // the merchant detail page. TCS Config / Intake Catalog are platform-wide configuration.
  const allMenuItems = [
    { name: 'Dashboard', path: '/', permission: Permission.DASHBOARD_VIEW },
    // Tenants (brand-website) — TENANT_VIEW gates the page, but MERCHANT-scoped
    // users also hold TENANT_VIEW purely so the API lets them read their own
    // (ECOMMERCE) tenant; the brand-website list would always be empty for them,
    // so hide the entry from MERCHANT scope. COLLECTIVE staff see all tenants and
    // TENANT-scoped users see their own.
    ...(user?.roleScope !== 'MERCHANT'
      ? [{ name: 'Tenants', path: '/tenants', permission: Permission.TENANT_VIEW, group: 'Commerce' }]
      : []),
    { name: 'Merchants', path: '/merchants', permission: Permission.MERCHANT_VIEW, group: 'Commerce' },
    // Read-only fleet view of go-live readiness across all storefront tenants (the
    // per-merchant provisioning ACTIONS stay on merchant detail — see note above).
    { name: 'Storefronts', path: '/storefronts', permission: Permission.MERCHANT_VIEW, group: 'Commerce' },
    { name: 'Orders', path: '/orders', permission: Permission.ORDER_READ, group: 'Commerce' },
    { name: 'Custom Orders', path: '/custom-orders', permission: Permission.ORDER_READ, group: 'Commerce', merchantScoped: true },
    { name: 'Booking Services', path: '/booking-services', permission: Permission.BOOKING_SERVICE_VIEW, group: 'Commerce', merchantScoped: true },
    {
      name: 'Product Catalogue',
      path: '/products',
      permissions: [Permission.PRODUCT_READ_ALL, Permission.PRODUCT_READ_PUBLISHED],
      group: 'Catalogue',
    },
    { name: 'Homepage Slider', path: '/homepage-slider', permission: Permission.SLIDER_VIEW, group: 'Catalogue', merchantScoped: true },
    { name: 'Invoices', path: '/invoices', permission: Permission.INVOICE_VIEW, group: 'Finance' },
    { name: 'Payments', path: '/payments', permission: Permission.PAYMENT_VIEW, group: 'Finance' },
    { name: 'Payouts', path: '/payouts', permission: Permission.SETTLEMENT_VIEW, group: 'Finance', merchantScoped: true },
    { name: 'Disputes', path: '/disputes', permission: Permission.PAYMENT_DISPUTE_VIEW, group: 'Finance' },
    { name: 'Reconciliation', path: '/reconciliation', permission: Permission.RECONCILIATION_VIEW, group: 'Finance' },
    { name: 'TCS Config', path: '/attribute-schemas', permission: Permission.PRODUCT_SCHEMA_MANAGE, group: 'Configuration' },
    { name: 'Intake Catalog', path: '/intake-catalog', permission: Permission.PRODUCT_SCHEMA_MANAGE, group: 'Configuration' },
    // Charter (PE-1831) — 5th TCS platform. Merchant back-office pages are merchantScoped
    // (visible to provider admins/users; COLLECTIVE staff manage cross-provider from the
    // "Providers"/"Oversight" entries below, which are NOT merchantScoped).
    { name: 'Charter Services', path: '/charter/services', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Packages', path: '/charter/catalogue', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Bookings', path: '/charter/bookings', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Orders', path: '/charter/orders', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Portfolio', path: '/charter/portfolio', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Waitlist', path: '/charter/waitlist', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Settings', path: '/charter/settings', permission: Permission.CHARTER_VIEW, group: 'Charter', merchantScoped: true },
    { name: 'Charter Providers', path: '/charter/providers', permission: Permission.CHARTER_ADMIN, group: 'Charter' },
    { name: 'Charter Oversight', path: '/charter/oversight', permission: Permission.CHARTER_ADMIN, group: 'Charter' },
    { name: 'TCS Team', path: '/staff', permission: Permission.STAFF_VIEW, group: 'Admin' },
    { name: 'Leads', path: '/leads', permission: Permission.LEADS_VIEW, group: 'Admin' },
    // NOT merchantScoped: audit-service scopes results server-side (merchant admins see only
    // their own merchant), so the page needs no merchant picker and stays in every sidebar.
    { name: 'Audit Logs', path: '/audit-logs', permission: Permission.AUDIT_LOG_VIEW, group: 'Admin' },
  ]
  const LOG_OUT_ITEM = { name: 'Log Out', path: '/logout', alwaysVisible: true }
  const GROUP_ORDER = ['Commerce', 'Catalogue', 'Finance', 'Charter', 'Configuration', 'Admin']

  const visible = allMenuItems.filter((item) => {
    // Users who can open merchant detail manage merchant-scoped pages there, so those are
    // hidden from their global sidebar. Users without MERCHANT_VIEW keep them (their only path).
    if (canManageViaMerchantDetail && 'merchantScoped' in item && item.merchantScoped) return false
    if ('permissions' in item && item.permissions) return hasAnyPermission(item.permissions)
    if ('permission' in item && item.permission) return hasPermission(item.permission)
    return false
  })
  const ungroupedItems = visible.filter((item) => !('group' in item) || !item.group)
  const groupedSections = GROUP_ORDER
    .map((group) => ({ group, items: visible.filter((item) => 'group' in item && item.group === group) }))
    .filter((section) => section.items.length > 0)

  // Collapsible groups: the group holding the current route starts open; the user can
  // toggle any group and their choice sticks for the session (overrides the default).
  const activeGroup = allMenuItems.find((item) => 'group' in item && item.group && location.pathname === item.path)?.group
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({})
  const isGroupOpen = (group: string) => openOverrides[group] ?? group === activeGroup
  const toggleGroup = (group: string) =>
    setOpenOverrides((prev) => ({ ...prev, [group]: !isGroupOpen(group) }))

  const handleItemClick = (item: { name: string; path: string }) => {
    if (item.name === 'Log Out') {
      logout()
      navigate('/login')
    } else {
      navigate(item.path)
    }
    // Close mobile sidebar after navigation
    onNavigate?.()
  }

  const renderItem = (item: { name: string; path: string }) => {
    const isActive = location.pathname === item.path || (location.pathname === '/' && item.path === '/dashboard')
    return (
      <button
        key={item.name}
        onClick={() => handleItemClick(item)}
        className={`w-full flex items-center gap-3 cursor-pointer px-3 py-2 text-left transition-colors text-[18px] rounded-md ${
          isActive
            ? 'bg-muted text-foreground font-[700]'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground font-[400]'
          }`}
      >
        {item.name}
      </button>
    )
  }

  return (
    <aside className="w-72 bg-card text-card-foreground border-r border-border h-full flex flex-col justify-between">
      {/* min-h-0 + overflow-y-auto so a long/COLLECTIVE-scoped menu can still scroll; Log Out
          is pinned below (outside this scroll area, alongside "Powered by") so it's always
          reachable regardless of list length or scroll position. */}
      <div className="flex-1 min-h-0 overflow-y-auto themed-scrollbar">
        {user?.roleScope === 'COLLECTIVE' && (
          <div className="w-[229px] mx-auto p-4 pt-8 pb-0 lg:hidden">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Merchant Filter</p>
            <MerchantPicker />
          </div>
        )}
        <nav className="w-[229px] mx-auto p-4 pt-8 lg:pt-20 space-y-1">
          {ungroupedItems.map(renderItem)}
        </nav>
        {groupedSections.map(({ group, items }) => {
          const open = isGroupOpen(group)
          const containsActive = items.some((item) => location.pathname === item.path)
          return (
            <nav key={group} className="w-[229px] mx-auto px-4 pt-4 space-y-1">
              <button
                onClick={() => toggleGroup(group)}
                aria-expanded={open}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors hover:text-foreground ${
                  containsActive && !open ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {group}
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none"
                  className={`transition-transform ${open ? 'rotate-90' : ''}`}
                >
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {open && items.map(renderItem)}
            </nav>
          )
        })}
        <div className="h-4" />
      </div>
      <div className="w-[229px] mx-auto px-4 pt-3 border-t border-border">
        {renderItem(LOG_OUT_ITEM)}
      </div>
      <div className="w-[229px] mx-auto p-4 pt-2 mb-6 text-[12px] text-muted-foreground">
        Powered by <a href="https://thecollectivesynergy.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground font-semibold hover:underline">The Collective Synergy</a>
      </div>
    </aside>
  )
}
